import React, { useState, useEffect } from "react";
import { Input } from ".././input";

interface DatePickerMMDDYYYYProps {
    value?: string; // Format: "YYYY-MM-DD" for internal use
    onChange?: (value: string) => void;
    disabled?: boolean;
    className?: string;
    placeholder?: string;
}

export function DatePickerMMDDYYYY({ 
    value, 
    onChange, 
    disabled, 
    className,
    placeholder = "mm/dd/yyyy"
}: DatePickerMMDDYYYYProps) {
    // Convert YYYY-MM-DD to MM/DD/YYYY for display
    const formatToDisplay = (dateStr: string) => {
        if (!dateStr) return "";
        const [year, month, day] = dateStr.split("-");
        return `${month}/${day}/${year}`;
    };

    // Convert MM/DD/YYYY to YYYY-MM-DD for storage
    const formatToStorage = (displayStr: string) => {
        // Remove non-numeric characters except /
        const cleaned = displayStr.replace(/[^\d/]/g, "");
        const parts = cleaned.split("/");
        
        if (parts.length === 3) {
            const [month, day, year] = parts;
            
            // Validate
            const monthNum = parseInt(month);
            const dayNum = parseInt(day);
            const yearNum = parseInt(year);
            
            if (monthNum >= 1 && monthNum <= 12 && 
                dayNum >= 1 && dayNum <= 31 && 
                yearNum >= 1900 && yearNum <= 2100) {
                return `${year.padStart(4, "0")}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
            }
        }
        return "";
    };

    const [displayValue, setDisplayValue] = useState(formatToDisplay(value || ""));

    useEffect(() => {
        setDisplayValue(formatToDisplay(value || ""));
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let input = e.target.value;
        
        // Remove non-numeric characters except /
        input = input.replace(/[^\d/]/g, "");
        
        // Auto-format as user types
        let formatted = input;
        
        // Auto-add slashes
        if (input.length >= 2 && !input.includes("/")) {
            formatted = input.slice(0, 2) + "/" + input.slice(2);
        }
        if (input.length >= 5 && input.split("/").length === 2) {
            const parts = input.split("/");
            formatted = parts[0] + "/" + parts[1].slice(0, 2) + "/" + parts[1].slice(2);
        }
        
        // Limit length
        const slashCount = (formatted.match(/\//g) || []).length;
        if (slashCount === 2) {
            const parts = formatted.split("/");
            if (parts[2].length > 4) {
                parts[2] = parts[2].slice(0, 4);
                formatted = parts.join("/");
            }
        }
        
        setDisplayValue(formatted);
    };

    const handleBlur = () => {
        const storageFormat = formatToStorage(displayValue);
        if (storageFormat) {
            onChange?.(storageFormat);
            setDisplayValue(formatToDisplay(storageFormat));
        } else if (displayValue) {
            // Invalid format, reset to previous valid value
            setDisplayValue(formatToDisplay(value || ""));
        } else {
            onChange?.("");
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Allow: backspace, delete, tab, escape, enter
        if ([8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
            // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
            (e.keyCode === 65 && e.ctrlKey === true) ||
            (e.keyCode === 67 && e.ctrlKey === true) ||
            (e.keyCode === 86 && e.ctrlKey === true) ||
            (e.keyCode === 88 && e.ctrlKey === true) ||
            // Allow: home, end, left, right
            (e.keyCode >= 35 && e.keyCode <= 39)) {
            return;
        }
        // Ensure that it is a number or slash and stop the keypress if not
        if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && 
            (e.keyCode < 96 || e.keyCode > 105) && 
            e.keyCode !== 191 && e.keyCode !== 111) {
            e.preventDefault();
        }
    };

    return (
        <Input
            type="text"
            value={displayValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            className={className}
            placeholder={placeholder}
            maxLength={10}
        />
    );
}

